import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';

import {
  nextDateSlot,
  prevDateSlot,
  updateAppointment,
  updateAppointmentDate,
} from 'actions';
import { getAvailableDates } from 'actions/BookingActions';
import { trackEvent } from 'integrations/metrics';
import { EventNames } from 'integrations/metrics/events';
import { EventAction, EventObjectType } from 'integrations/metrics/types';
import moment from 'moment-timezone';
import { compose } from 'redux';
import { getSelectedServices } from 'selectors/services';

import Button from 'components/Button';
import { Calendar } from 'components/Calendar/Calendar';
import Modal from 'components/Modal/Modal';
import SalonContact from 'components/SalonContact/SalonContact';

import JoinWaitlistButton from 'routes/waitlist/JoinButton/JoinButton';

import { getOrderedServiceGuids } from 'utils/appointmentPlanUtils';

import AppointmentInfo from '../AppointmentInfo';

import s from './SelectDate.module.css';

const momentFormat = 'ddd MMM DD YYYY';

const MIN_SERVICE_COUNT_FOR_SMART_SERVICE_ORDERING = 2;

class SelectDate extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedDay: props.selectedDate || null,
      showingError: false,
      currentDate: new Date(),
      loading: true,
      isBookingErrorModalVisible: props.bookingError && props.isReschedule,
      orderedServices: null, // Cache the ordered services
    };

    this.handleDayClick = this.handleDayClick.bind(this);
    this.handleMonthChange = this.handleMonthChange.bind(this);
    this.onClickNext = this.onClickNext.bind(this);
    this.goToNextStep = this.goToNextStep.bind(this);
  }

  async componentDidMount() {
    try {
      const serviceGuids = this.props.appointment.service_guids;

      // Check if we have service guids, if not, we can't proceed
      // TODO: EN-44303 Improve UX - redirect to /services or show error message instead of empty page
      // This typically happens when user directly accesses /book URL without selecting services
      if (!serviceGuids || serviceGuids.length === 0) {
        this.setState({ loading: false });
        return;
      }

      // Get ordered services using the appointment plan API if feature is enabled and we have multiple services
      let orderedServices = serviceGuids;

      if (
        this.props.isSmartServiceOrderingEnabled &&
        serviceGuids.length >= MIN_SERVICE_COUNT_FOR_SMART_SERVICE_ORDERING
      ) {
        orderedServices = await getOrderedServiceGuids({
          serviceGuids,
          users: this.props.users,
          apiUrlV3: this.props.apiUrlV3,
          businessToken: this.props.businessToken,
        });
      }

      // Cache the ordered services for reuse in month changes
      this.setState({ orderedServices });

      // Update the Redux store with ordered services so other components can access them
      if (orderedServices !== serviceGuids) {
        this.props.updateAppointment('service_guids', orderedServices);
      }

      await this.props.getAvailableDates({
        slug: this.props.publicUser.slug,
        date: moment().toDate(),
        service_guids: orderedServices,
        appointment_guid: this.props.appointment.appointmentGuid,
        appointment_token: this.props.appointment.appointmentToken,
      });
    } catch (error) {
      console.error('Error loading available dates:', error);
    }

    this.setState({ loading: false });
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.bookingError !== prevProps.bookingError &&
      this.props.bookingError &&
      this.props.isReschedule
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isBookingErrorModalVisible: true });
    }
  }

  handleDayClick(day, { disabled, selected }) {
    if (selected || disabled) {
      return false;
    }
    const date = moment(day.toDateString(), momentFormat);

    this.setState({
      selectedDay: selected ? null : date,
    });

    if (!selected) {
      this.props.handleDateClick(date);
    }
  }

  handleMonthChange(month) {
    const date = moment(month.toDateString(), momentFormat);

    this.setState({ currentDate: date });

    const serviceGuids =
      this.state.orderedServices || this.props.appointment.service_guids;

    // Don't fetch if we don't have service guids
    if (!serviceGuids || serviceGuids.length === 0) {
      return;
    }

    this.props.getAvailableDates({
      slug: this.props.publicUser.slug,
      date,
      service_guids: serviceGuids,
    });
  }

  onClickNext() {
    if (this.props.bookingError && this.props.isReschedule) {
      this.setState({
        isBookingErrorModalVisible: true,
      });

      return false;
    }

    if (!this.props.selectedDate) {
      if (!this.state.showingError) {
        this.setState({ showingError: true });
        setTimeout(() => {
          this.setState({ showingError: false });
        }, 500);
      }
      return false;
    }

    this.goToNextStep();
  }

  goToNextStep() {
    trackEvent(EventNames.NEXT_BUTTON_CLICKED_DATE_TIME, {
      action: EventAction.SELECT,
      productSection: 'Booking',
      objectType: EventObjectType.BUTTON,
      reschedule: this.props.isReschedule,
      bundle_size: 1,
      is_msmp_booking: false,
      page_identifier: 'date',
      service_ids: this.props.selectedServices,
    });

    this.props.navigateToNextStep({
      stepKey: 'select-time',
      action: this.props.isReschedule ? 'reschedule' : undefined,
    });
  }

  render() {
    const {
      bookingError,
      businessEmail,
      businessName,
      businessPhone,
      cellPhone,
      isReschedule,
      isWaitlistEnabled,
    } = this.props;
    const { selectedDay, isBookingErrorModalVisible } = this.state;

    const action = isReschedule ? 'reschedule' : 'schedule';

    return (
      <div className={s.root}>
        {isBookingErrorModalVisible && bookingError && (
          <Modal
            title={`Can’t ${action} online`}
            onClose={() => {
              this.setState({ isBookingErrorModalVisible: false });
            }}
          >
            {`Sorry, you can’t ${action} your appointment online! Please contact ${businessName}`}
            <SalonContact
              businessEmail={businessEmail}
              businessPhone={businessPhone}
              cellPhone={cellPhone}
            />
            {`to ${action}.`}
          </Modal>
        )}

        <div className={s.outerDatePickerWrap}>
          <Calendar
            selectedDay={selectedDay}
            dateSlots={this.props.dateSlots}
            loading={this.state.loading || this.props.appointment?.loading}
            showingError={this.state.showingError}
            handleMonthChange={this.handleMonthChange}
            handleDayClick={this.handleDayClick}
          />
        </div>

        <Button className={s.button} onClick={this.onClickNext}>
          Next
        </Button>

        {isWaitlistEnabled && <JoinWaitlistButton />}

        <AppointmentInfo />
      </div>
    );
  }
}

const SelectDateComponent = compose(
  withRouter,
  connect(
    (state, ownProps) => {
      const isReschedule = ownProps.router.query.action === 'reschedule';

      return {
        slug: state.publicUser.slug,
        slotIndex: state.appointment?.slotIndex,
        selectedDate: state.appointment?.date,
        selectedServices: getSelectedServices(state),
        dateSlots: state.appointment?.dateSlots,
        appointment: state.appointment,
        publicUser: state.publicUser,
        isMultiUser: state.publicUser.multi_user,
        businessEmail: state.publicUser.business_email,
        businessName: state.publicUser.business_name,
        businessPhone: state.publicUser.business_phone,
        cellPhone: state.publicUser.phone,
        bookingError: state.appointment?.error,
        isReschedule,
        isWaitlistEnabled:
          state.publicUser.preferences.waitlist_registration_enabled &&
          !isReschedule,
      };
    },
    (dispatch) => ({
      handleDateClick(date) {
        if (date) {
          dispatch(updateAppointmentDate('date', date));
        }
      },
      getAvailableDates(params) {
        dispatch(getAvailableDates(params));
      },
      handleNextClick() {
        dispatch(nextDateSlot());
      },
      handlePrevClick() {
        dispatch(prevDateSlot());
      },
      updateAppointment(field, value) {
        dispatch(updateAppointment(field, value));
      },
    }),
  ),
)(SelectDate);

export default SelectDateComponent;
